import logger from '../utils/logger';
import React, { useState, useEffect, useContext, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import "../style/Payment.css";
import { getUserId, allocateAmount } from "../utils/utils";
import { GlobalStateContext } from "../utils/GlobalStateContext";
import TerminalCheckoutScreen from "./TerminalCheckoutScreen";
// const publishableKey = process.env.REACT_APP_PUBLISHABLE_KEY;
const publishableKey =
  "pk_live_51SPmXhQoahQ6vZZIGR0wMAwX7bc6OgYFcV40CTMwZbzT9vGn5gBwieh5GSuTT92L2igBhnQSPqSGtk2LBL12Qyuo00ZbJUREMl";
  // "pk_test_51SPmXrJZn0RAcMM5rpFoXzihUuWgfHY21fU2QRqK2N4Nh0h7i5DCEjHvj1woltDBmDAT8joqe8iTDtQ4yFw9wszE00rEguwv9l";
// logger.log(publishableKey);
const stripePromise = loadStripe(publishableKey);

// Stripe processing fee rates (base rates — never surcharge above these)
// Only applied to credit cards; debit/prepaid/unknown are exempt by law.
const STRIPE_FEES = {
  card: { rate: 0.029 },       // 2.9% (manual card entry)
  terminal: { rate: 0.027 },   // 2.7% (in-person tap/dip/swipe)
};

// One key per payment submission, minted when the cash/check screen mounts. It rides along on
// the POST so the server can absorb a double click or a client retry instead of recording the
// payment twice (for a combined collection that would duplicate the whole allocation set).
// Going back to the amount screen and starting over remounts the screen, so a genuinely
// separate second payment gets a fresh key and records normally.
const newIdempotencyKey = () => `idem_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

// Calculate the processing fee for a given amount (in cents).
// Uses the formula: fee = (rate * amount) / (1 - rate)
// This accounts for Stripe charging fees on the total (including the surcharge).
const calculateProcessingFee = (amountCents, method) => {
  const feeConfig = STRIPE_FEES[method];
  if (!feeConfig) return 0;
  const fee = (feeConfig.rate * amountCents) / (1 - feeConfig.rate);
  return Math.round(fee); // round to nearest cent
};

const CheckPaymentScreen = ({
  paymentInfo,
  patients_id,
  payment_id,
  appointmentId,
  isGroup,
  groupWeights,
  group_ref,
  onCheckPaymentComplete,
  onBack
}) => {
  const location = useLocation();
  const [checkNumber, setCheckNumber] = useState('');
  const [payerName, setPayerName] = useState('');
  const [bankName, setBankName] = useState('');
  const [memo, setMemo] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const idempotencyKey = useRef(newIdempotencyKey());
  const BASE_URL = process.env.REACT_APP_API_URL;
  const amountOwed = paymentInfo.amount;
  const { appointmentId: locationAppointmentId } = location.state || {};
  const { currentUser } = useContext(GlobalStateContext);
  const user = getUserId(currentUser);

  const handleConfirmCheckPayment = async () => {
    if (submitting || confirmed) return;   // a combined collection takes seconds — don't let a second click through
    if (!checkNumber.trim()) {
      setError('Check number is required.');
      return;
    }
    if (!payerName.trim()) {
      setError('Payer name is required.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/payments/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_due: amountOwed,
          amount_paid: amountOwed,
          balance_remaining: 0,
          check_number: checkNumber.trim(),
          payer_name: payerName.trim(),
          bank_name: bankName.trim() || null,
          memo: memo.trim() || null,
          ris_patients_id: patients_id,
          payment_id,
          schedule_id: locationAppointmentId || appointmentId,
          allocations: isGroup ? allocateAmount(groupWeights, amountOwed) : null,
          group_ref: group_ref || null,
          idempotency_key: idempotencyKey.current,
          description: paymentInfo.chargeDescription || null,
          type: currentUser.type,
          user
        })
      });
      if (!response.ok) {
        setError('Failed to record check payment. Please try again.');
        return;
      }
      setConfirmed(true);
      if (typeof onCheckPaymentComplete === 'function') onCheckPaymentComplete();
    } catch (err) {
      logger.error('Failed to record check payment:', err);
      setError('An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cash-payment-screen">
      <h2>Check Payment</h2>
      <div className="payment-summary">
        <div><strong>Amount Owed:</strong> ${(amountOwed / 100).toFixed(2)}</div>
      </div>
      <br />
      <label>
        <strong>Check Number *</strong>
        <input
          type="text"
          value={checkNumber}
          onChange={(e) => setCheckNumber(e.target.value)}
          placeholder="e.g. 1042"
          disabled={confirmed}
        />
      </label>
      <label>
        <strong>Name on Check *</strong>
        <input
          type="text"
          value={payerName}
          onChange={(e) => setPayerName(e.target.value)}
          placeholder="e.g. John Smith"
          disabled={confirmed}
        />
      </label>
      <label>
        Bank Name (optional)
        <input
          type="text"
          value={bankName}
          onChange={(e) => setBankName(e.target.value)}
          placeholder="e.g. Commerce Bank"
          disabled={confirmed}
        />
      </label>
      <label>
        Memo (optional)
        <input
          type="text"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="e.g. imaging visit"
          disabled={confirmed}
        />
      </label>
      {error && <div className="payment-error">{error}</div>}
      {!confirmed && (
        <>
          <br />
          <button className="PrimaryButton" onClick={handleConfirmCheckPayment} disabled={submitting}>
            {submitting ? 'Recording…' : 'Confirm Check Payment'}
          </button>
          {onBack && (
            <button className="PrimaryButton" style={{ marginLeft: 8 }} onClick={onBack} disabled={submitting}>
              Back
            </button>
          )}
        </>
      )}
      {confirmed && (
        <>
          <br />
          <div className="confirmation-message">
            ✅ Check payment confirmed. Check #{checkNumber}
          </div>
        </>
      )}
    </div>
  );
};

const CashPaymentScreen = ({
  paymentInfo,
  patients_id,
  payment_id,
  isGroup,
  groupWeights,
  group_ref,
  onPayRemainingWithCard,
  onCashPaymentComplete,
  onBack
}) => {
  const location = useLocation();
  const [cashPaid, setCashPaid] = useState(0); // cents
  const [cashPaidInput, setCashPaidInput] = useState("0.00"); // string for input
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const idempotencyKey = useRef(newIdempotencyKey());
  const BASE_URL = process.env.REACT_APP_API_URL;
  const amountOwed = paymentInfo.amount;
  const amountDue = Math.max(amountOwed - cashPaid, 0);
  const changeDue = cashPaid > amountOwed ? cashPaid - amountOwed : 0;
  const { appointmentId } = location.state || {};
  const { currentUser } = useContext(GlobalStateContext);
  const user = getUserId(currentUser);

  const handleCashPaidChange = (e) => {
    setCashPaidInput(e.target.value);
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setCashPaid(value * 100);
    } else {
      setCashPaid(0);
    }
    setConfirmed(false); // reset confirmation when editing
  };

  // Record the cash actually applied toward the bill (excludes change). For a combined
  // collection, allocate that applied amount across appointments by their balances.
  const recordCash = async () => {
    const appliedCents = Math.min(cashPaid, amountOwed);
    setSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/payments/cash`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount_due: amountOwed,
          amount_paid: cashPaid,
          change_due: changeDue,
          balance_remaining: amountDue,
          ris_patients_id: patients_id,
          payment_id,
          schedule_id: appointmentId,
          allocations: isGroup ? allocateAmount(groupWeights, appliedCents) : null,
          group_ref: group_ref || null,
          idempotency_key: idempotencyKey.current,
          description: paymentInfo.chargeDescription || null,
          type: currentUser.type,
          user
        })
      });
      if (!response.ok) logger.error('Failed to record cash payment');
    } catch (dbErr) {
      logger.error("Failed to record cash payment:", dbErr);
    } finally {
      setSubmitting(false);
    }
  };

  // Full payment (no balance left) or partial recorded as self-pay (balance left owed).
  // A combined collection takes seconds to record, so both entry points bail on a second
  // click rather than sending the allocation set again.
  const finalizeCash = async () => {
    if (submitting || confirmed) return;
    await recordCash();
    setConfirmed(true);
    if (typeof onCashPaymentComplete === "function") {
      onCashPaymentComplete({ paidAmount: cashPaid, changeDue });
    }
  };

  // Record the cash portion, then collect the remaining balance by card.
  const handlePartialThenCard = async () => {
    if (submitting || confirmed) return;
    await recordCash();
    onPayRemainingWithCard(amountDue);
  };

  return (
    <div className="cash-payment-screen">
      <h2>Cash Payment</h2>

      <div className="payment-summary">
        <div>
          <strong>Amount Owed:</strong> ${(amountOwed / 100).toFixed(2)}
        </div>
        <div>
          <label>
            <strong>Amount Paid:</strong>
            <input
              type="number"
              min="0"
              step="0.01"
              value={cashPaidInput}
              onChange={handleCashPaidChange}
            />
          </label>
        </div>
        <div>
          <strong>Amount Due:</strong> ${(amountDue / 100).toFixed(2)}
        </div>
        <div>
          <strong>Change Due:</strong> ${(changeDue / 100).toFixed(2)}
        </div>
      </div>

      {!confirmed && amountDue > 0 && (
        <>
          <br />
          <button className="PrimaryButton" onClick={handlePartialThenCard} disabled={submitting}>
            {submitting ? 'Recording…' : `Pay Remaining $${(amountDue / 100).toFixed(2)} with Card`}
          </button>
          <button className="PrimaryButton" style={{ marginLeft: 8 }} onClick={finalizeCash} disabled={submitting}>
            {submitting ? 'Recording…' : `Bill Remaining $${(amountDue / 100).toFixed(2)} as Self-Pay`}
          </button>
        </>
      )}

      {!confirmed && amountDue === 0 && (
        <>
          <br />
          <button className="PrimaryButton" onClick={finalizeCash} disabled={submitting}>
            {submitting ? 'Recording…' : 'Confirm Cash Payment'}
          </button>
        </>
      )}

      {!confirmed && onBack && (
        <>
          <br />
          <button className="PrimaryButton" style={{ marginTop: 8 }} onClick={onBack} disabled={submitting}>
            Back
          </button>
        </>
      )}

      {confirmed && (
        <>
          <br />
          <div className="confirmation-message">
            ✅ Cash payment of ${(Math.min(cashPaid, amountOwed) / 100).toFixed(2)} recorded.{" "}
            {changeDue > 0 && `Change due: $${(changeDue / 100).toFixed(2)}. `}
            {amountDue > 0 && `Remaining $${(amountDue / 100).toFixed(2)} billed as self-pay.`}
          </div>
        </>
      )}
    </div>
  );
};

const OfficeAssistantAmountScreen = ({ onAmountSelected }) => {
  const { currentUser } = useContext(GlobalStateContext);
  const location = useLocation();
  const { totalCost, totalPaid, payment_id, appointmentId, group } = location.state || {};
  const isGroup = !!group;
  // For a combined collection, identity/total come from the group payload.
  const patients_id = isGroup ? group.patients_id : location.state?.patients_id;
  const patientName = isGroup ? group.patientName : location.state?.patientName;
  const email = isGroup ? group.email : location.state?.email;
  // Prefer the authoritative balance computed upstream (handles discrete add-on billing where
  // total - totalPaid would understate or go negative); fall back to the naive difference.
  const explicitBalanceDue = location.state?.balanceDue;
  const DEFAULT_AMOUNT = isGroup
    ? Math.round((group.combinedTotal || 0) * 100)
    : (explicitBalanceDue != null
        ? Math.round(explicitBalanceDue * 100)
        : (totalCost ? (totalCost - (totalPaid)) * 100 : 0));
  // logger.log(DEFAULT_AMOUNT)
  const [amount, setAmount] = useState(DEFAULT_AMOUNT); // cents
  const [amountInput, setAmountInput] = useState(
    (DEFAULT_AMOUNT / 100).toFixed(2)
  ); // string for input
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const user = getUserId(currentUser);
  const type = currentUser.type;

  const handleAmountChange = (e) => {
    setAmountInput(e.target.value);
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setAmount(Math.round(value * 100));
    } else {
      setAmount(0);
    }
  };

  const processingFee = (paymentMethod === 'card' || paymentMethod === 'terminal')
    ? calculateProcessingFee(amount, paymentMethod)
    : 0;
  const totalWithFee = amount + processingFee;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!amount || isNaN(amount) || amount < 100) {
      setError("Please enter a valid amount (minimum $1.00)");
      return;
    }
    if (!paymentMethod) {
      setError("Please select a payment method");
      return;
    }
    setError(null);

    // Pass the per-appointment weights through untouched; each payment screen scales them
    // to the actual amount it collects (so partial payments allocate correctly).
    onAmountSelected({
      amount: Number(totalWithFee),
      baseAmount: Number(amount),
      processingFee: Number(processingFee),
      method: paymentMethod,
      patients_id,
      payment_id,
      appointmentId,
      patientName,
      email,
      user,
      type,
      isGroup,
      groupWeights: isGroup ? group.allocations : null,
      group_ref: isGroup ? group.group_ref : null,
      chargeDescription: location.state?.chargeDescription || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <h2 style={{ fontSize: "1.25rem", marginBottom: "1rem" }}>
        Enter Payment Amount
      </h2>
      <label style={{ display: "block", fontSize: "1rem", marginBottom: "0.5rem" }}>
        Amount Due ($)
        <input
          type="number"
          inputMode="decimal"
          pattern="[0-9]*"
          min="1"
          step="0.01"
          value={amountInput}
          onChange={handleAmountChange}
          required
          style={{
            width: "100%",
            padding: "0.75rem",
            fontSize: "1rem",
            borderRadius: "8px",
            border: "1px solid #ccc",
            marginTop: "0.5rem",
            boxSizing: "border-box"
          }}
        />
      </label>

      <div className="payment-method-selection">
        <h3>Select Payment Method</h3>
        <div className="payment-method-cards">
          <div
            className={`method-card ${
              paymentMethod === "cash" ? "selected" : ""
            }`}
            onClick={() => setPaymentMethod("cash")}
          >
            <h4>💵 Cash</h4>
            <p>Pay with physical currency</p>
          </div>
          <div
            className={`method-card ${
              paymentMethod === "card" ? "selected" : ""
            }`}
            onClick={() => setPaymentMethod("card")}
          >
            <h4>💳 Card</h4>
            <p>Manually Enter Card Details</p>
          </div>
          <div
            className={`method-card ${paymentMethod === "terminal" ? "selected" : ""}`}
            onClick={() => setPaymentMethod("terminal")}
          >
            <h4>🧾 Terminal</h4>
            <p>Swipe, Insert, or Tap Card</p>
          </div>
          <div
            className={`method-card ${paymentMethod === "check" ? "selected" : ""}`}
            onClick={() => setPaymentMethod("check")}
          >
            <h4>🖊️ Check</h4>
            <p>Pay by personal or business check</p>
          </div>
        </div>
      </div>

      {processingFee > 0 && (
        <div className="fee-breakdown">
          <div><strong>Subtotal:</strong> ${(amount / 100).toFixed(2)}</div>
          <div><strong>Processing Fee ({paymentMethod === 'terminal' ? '2.7%' : '2.9%'}):</strong> ${(processingFee / 100).toFixed(2)}</div>
          <div><strong>Total:</strong> ${(totalWithFee / 100).toFixed(2)}</div>
          <div className="fee-notice">* Processing fee applies to credit cards only. Debit cards are not surcharged.</div>
        </div>
      )}

      <button type="submit">Continue to Payment</button>
      {error && <div className="payment-error">{error}</div>}
    </form>
  );
};

const CheckoutForm = ({ amount, baseAmount, processingFee, patients_id, payment_id, allocations, group_ref, chargeDescription, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [fundingType, setFundingType] = useState(null); // "credit", "debit", "prepaid", "unknown"
  const BASE_URL = process.env.REACT_APP_API_URL;
  const {currentUser} = useContext(GlobalStateContext);
  const user = getUserId(currentUser);

  // Determine the actual charge amount based on funding type.
  // Debit/prepaid/unknown cards cannot legally be surcharged.
  const isSurchargeable = fundingType === "credit";
  const actualFee = isSurchargeable ? processingFee : 0;
  const chargeAmount = baseAmount + actualFee;

  const handleSubmit = async (e) => {
    e.preventDefault();

    setProcessing(true);
    setError(null);
    setSuccess(null);

    if (!stripe || !elements) {
      logger.error("Stripe or Elements not ready");
      setProcessing(false);
      return;
    }

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        logger.error("CardElement is NULL");
        setProcessing(false);
        return;
      }

      // Step 1: Create a PaymentMethod to detect card funding type
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name, email }
      });

      if (pmError) {
        logger.error("PaymentMethod error:", pmError.message);
        setError(pmError.message);
        setProcessing(false);
        return;
      }

      const funding = paymentMethod.card?.funding; // "credit", "debit", "prepaid", or "unknown"
      setFundingType(funding);

      // Only surcharge credit cards — debit surcharging is illegal
      const applySurcharge = funding === "credit";
      const fee = applySurcharge ? processingFee : 0;
      const finalAmount = baseAmount + fee;

      // Step 2: Create PaymentIntent with the correct amount
      const res = await fetch(`${BASE_URL}/payments/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: finalAmount,
          name,
          email,
          patients_id,
          payment_id,
          user,
          type: currentUser.type,
          surcharge_amount: fee > 0 ? fee : undefined,
          original_amount: fee > 0 ? baseAmount : undefined,
          allocations: allocations || undefined,
          group_ref: group_ref || undefined,
          description: chargeDescription || undefined
        })
      });

      const json = await res.json();
      const { clientSecret, error: backendError } = json;

      if (backendError) {
        logger.error("Backend error:", backendError);
        setError(backendError);
        setProcessing(false);
        return;
      }

      if (!clientSecret) {
        logger.error("No clientSecret returned from backend");
        setError("No client secret received from server.");
        setProcessing(false);
        return;
      }

      // Step 3: Confirm payment using the already-created PaymentMethod
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id
      });

      if (result.error) {
        logger.error("Stripe error:", result.error.message);
        setError(result.error.message);
      } else if (result.paymentIntent?.status === "succeeded") {
        // Record the charge synchronously (don't rely on the Stripe webhook). The recorder
        // is idempotent, so if the webhook also fires it won't double-record. For a combined
        // collection the server splits the amount across appointments using the PI metadata.
        try {
          const response = await fetch(`${BASE_URL}/payments/recordCardCharge`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              paymentIntentId: result.paymentIntent.id,
              user,
              type: currentUser.type
            }),
          });
          if (!response.ok) {
            logger.error('Failed to record card charge');
          }
        } catch (error) {
          logger.error("Error recording card charge", error);
        }
        setSuccess("Payment successful!");
        navigate("/successfulPayment", { state: { subscription: false } });
      } else {
        logger.warn("Payment did not succeed, status:", result.paymentIntent?.status);
      }
    } catch (err) {
      logger.error("Unexpected error during payment flow:", err);
      setError("Unexpected error occurred. See console for details.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <h2>Make a Payment</h2>
      {processingFee > 0 && (
        <div className="fee-breakdown">
          <div><strong>Subtotal:</strong> ${(baseAmount / 100).toFixed(2)}</div>
          <div><strong>Processing Fee (2.9%):</strong> ${(processingFee / 100).toFixed(2)}</div>
          <div><strong>Total:</strong> ${(amount / 100).toFixed(2)}</div>
          {fundingType && fundingType !== "credit" ? (
            <div className="fee-notice">No processing fee — {fundingType} card detected. You will only be charged ${(baseAmount / 100).toFixed(2)}.</div>
          ) : (
            <div className="fee-notice">* Processing fee applies to credit cards only. Debit cards are not surcharged.</div>
          )}
        </div>
      )}
      {!processingFee && (
        <div className="payment-amount-display">
          <div>
            <span>Amount Due:</span>
            <span className="payment-amount-value">${(baseAmount / 100).toFixed(2)}</span>
          </div>
        </div>
      )}
      <label>
        Name on Card
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </label>
      <label>
        Email Receipt To
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </label>
      <label>
        Card Details
        <CardElement
          onReady={() => logger.log("CardElement ready")}
          onChange={(event) =>
            logger.log("CardElement changed:", {
              complete: event.complete,
              error: event.error,
              empty: event.empty
            })
          }
          onBlur={() => logger.log("CardElement blurred")}
          onFocus={() => logger.log("CardElement focused")}
        />
      </label>
      <button type="submit" disabled={!stripe || processing}>
        {processing ? "Processing..." : "Pay"}
      </button>
      {onBack && (
        <button type="button" className="PrimaryButton" onClick={onBack} disabled={processing} style={{ marginLeft: 8 }}>
          Back
        </button>
      )}
      {error && <div className="payment-error">{error}</div>}
      {success && <div className="payment-success">{success}</div>}
    </form>
  );
};

const Payment = () => {
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [remainingAmount, setRemainingAmount] = useState(null);
  const [cashPaymentComplete, setCashPaymentComplete] = useState(false);
  const [checkPaymentComplete, setCheckPaymentComplete] = useState(false);
  const location = useLocation();
  const { patient_id, appointment_id } = location.state || {};

  const handlePayRemainingWithCard = (amount) => {
    setRemainingAmount(amount);
  };

  const handleCashPaymentComplete = ({ paidAmount, changeDue }) => {
    setCashPaymentComplete(true);
  };

  const handleCheckPaymentComplete = () => {
    setCheckPaymentComplete(true);
  };

  // Return to the amount/method screen (Back button on a payment screen).
  const resetToAmountScreen = () => {
    setPaymentInfo(null);
    setRemainingAmount(null);
    setCashPaymentComplete(false);
    setCheckPaymentComplete(false);
  };

  const isGroup = !!paymentInfo?.isGroup;
  const groupWeights = paymentInfo?.groupWeights || null;
  // Card base is the remaining balance when collecting a card remainder after cash, else the full base.
  const baseForCard = remainingAmount != null ? remainingAmount : paymentInfo?.baseAmount;
  const cardAllocations = isGroup ? allocateAmount(groupWeights, baseForCard) : undefined;
  const terminalAllocations = isGroup ? allocateAmount(groupWeights, paymentInfo?.baseAmount) : undefined;

  return (
    <div className="payment-container">
      <Elements stripe={stripePromise}>
        {paymentInfo === null ? (
          <OfficeAssistantAmountScreen onAmountSelected={setPaymentInfo} />
        ) : paymentInfo.method === "card" || remainingAmount !== null ? (
          <CheckoutForm amount={remainingAmount ?? paymentInfo.amount} baseAmount={remainingAmount ? remainingAmount : paymentInfo.baseAmount} processingFee={remainingAmount ? 0 : paymentInfo.processingFee} patients_id={patient_id || paymentInfo.patients_id} payment_id={paymentInfo.payment_id} allocations={cardAllocations} group_ref={paymentInfo.group_ref} chargeDescription={paymentInfo.chargeDescription} onBack={resetToAmountScreen} />
        ) : paymentInfo.method === "terminal" ? (
          <TerminalCheckoutScreen amount={Math.round(paymentInfo.amount)} baseAmount={paymentInfo.baseAmount} processingFee={paymentInfo.processingFee} patients_id={patient_id || paymentInfo.patients_id} payment_id={paymentInfo.payment_id} name={paymentInfo.patientName} email={paymentInfo.email} user={paymentInfo.user} type={paymentInfo.type} allocations={terminalAllocations} group_ref={paymentInfo.group_ref} chargeDescription={paymentInfo.chargeDescription} onBack={resetToAmountScreen}/>
        ) : paymentInfo.method === "check" && !checkPaymentComplete ? (
          <CheckPaymentScreen
            paymentInfo={paymentInfo}
            patients_id={patient_id || paymentInfo.patients_id}
            payment_id={paymentInfo.payment_id}
            appointmentId={paymentInfo.appointmentId}
            isGroup={isGroup}
            groupWeights={groupWeights}
            group_ref={paymentInfo.group_ref}
            onCheckPaymentComplete={handleCheckPaymentComplete}
            onBack={resetToAmountScreen}
          />
        ) : checkPaymentComplete ? (
          <div className="payment-confirmation">
            <h2>✅ Payment Successful</h2>
            <p>Thank you! The check payment has been recorded.</p>
            <Link to="/HGIPayments">Back to Payments</Link>
          </div>
        ) : cashPaymentComplete ? (
          <div className="payment-confirmation">
            <h2>✅ Payment Successful</h2>
            <p>Thank you! The full amount has been paid in cash.</p>
            <Link to="/HGIPayments">Back to Payments</Link>
          </div>
        ) : (
          <CashPaymentScreen
            paymentInfo={paymentInfo}
            patients_id={patient_id || paymentInfo.patients_id}
            payment_id={paymentInfo.payment_id}
            appointmentId={paymentInfo.appointmentId}
            isGroup={isGroup}
            groupWeights={groupWeights}
            group_ref={paymentInfo.group_ref}
            onPayRemainingWithCard={handlePayRemainingWithCard}
            onCashPaymentComplete={handleCashPaymentComplete}
            onBack={resetToAmountScreen}
          />
        )}
      </Elements>

    </div>
  );
};

// Named exports for tests only (Payment.doubleSubmit.test.js). The app renders these through
// <Payment />; exporting them lets the double-submit guard be tested without standing up
// Stripe Elements and the router state the parent needs.
export { CashPaymentScreen, CheckPaymentScreen };

export default Payment;
